import React, { Component } from 'react';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { FormattedMessage } from '../../util/reactIntl';
import { ensureCurrentUser } from '../../util/data';
import { isUserAuthorized } from '../../util/userHelpers';
import { matchPathname } from '../../util/routes';

import { Modal } from '../../components';

import EmailReminder from './EmailReminder';
import CalendarSyncReminder from './CalendarSyncReminder';
import css from './ModalMissingInformation.module.css';

const MISSING_INFORMATION_MODAL_WHITELIST = [
  'LoginPage',
  'SignupPage',
  'ContactDetailsPage',
  'EmailVerificationPage',
  'PasswordResetPage',
  'GoogleCalendarPage',
  'EditListingPage',
  'NewListingPage',
];

const EMAIL_VERIFICATION = 'EMAIL_VERIFICATION';
const CALENDAR_SYNC = 'CALENDAR_SYNC';

const getUserId = user => user?.id?.uuid || user?.id;

const isEmailVerificationDismissed = userId => {
  if (typeof window === 'undefined' || !userId) return false;
  return window.sessionStorage.getItem(`emailVerificationDismissed_${userId}`) === 'true';
};

const setEmailVerificationDismissed = userId => {
  if (typeof window !== 'undefined' && userId) {
    window.sessionStorage.setItem(`emailVerificationDismissed_${userId}`, 'true');
  }
};

const clearEmailVerificationDismissed = userId => {
  if (typeof window !== 'undefined' && userId) {
    window.sessionStorage.removeItem(`emailVerificationDismissed_${userId}`);
  }
};

class ModalMissingInformation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showMissingInformationReminder: null,
      hasSeenMissingInformationReminder: false,
    };
    this.handleMissingInformationReminder = this.handleMissingInformationReminder.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { currentUser, currentUserHasListings, currentUserHasOrders, location } = this.props;
    const user = ensureCurrentUser(currentUser);
    const prevUser = prevProps ? ensureCurrentUser(prevProps.currentUser) : null;

    const userId = getUserId(user);
    const prevUserId = getUserId(prevUser);

    if (prevUserId && prevUserId !== userId) {
      clearEmailVerificationDismissed(prevUserId);
      this.setState({
        showMissingInformationReminder: null,
        hasSeenMissingInformationReminder: false,
      });
    }

    this.handleMissingInformationReminder(
      user,
      currentUserHasListings,
      currentUserHasOrders,
      location
    );
  }

  handleMissingInformationReminder(
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    newLocation
  ) {
    const userId = getUserId(currentUser);
    const hasSeenInSession = isEmailVerificationDismissed(userId);
    const hasSeen = this.state.hasSeenMissingInformationReminder || hasSeenInSession;

    const routes = this.props.routeConfiguration;
    const matchedRoutes = matchPathname(newLocation.pathname, routes);
    const isPageWhitelisted = matchedRoutes.some(m =>
      MISSING_INFORMATION_MODAL_WHITELIST.includes(m.route.name)
    );

    // If user has already dismissed reminder for this session, do not show again
    if (hasSeen) {
      return;
    }

    const notRemindedYet = !this.state.showMissingInformationReminder;

    if (!isPageWhitelisted && notRemindedYet) {
      const userType = currentUser?.attributes?.profile?.publicData?.userType;
      const isServiceProvider = userType === 'service_provider' || userType === 'serviceprovider';
      const isGuest = !userType || userType === 'customer' || userType === 'guest' || !isServiceProvider;

      // For Service Providers, check if Google Calendar is connected
      if (isServiceProvider) {
        const publicData = currentUser?.attributes?.profile?.publicData || {};
        const isCalendarSynced = !!publicData.googleCalendarConnected;
        if (!isCalendarSynced) {
          this.setState({ showMissingInformationReminder: CALENDAR_SYNC });
          return;
        }
      }

      // Hide confirm email modal for guest users
      if (isGuest) {
        return;
      }

      // Emails are sent when order is initiated
      // Customer is likely to get email soon when she books something
      // Provider email should work - she should get an email when someone books a listing
      const hasOrders = currentUserHasOrders === true;
      const hasListingsOrOrders = currentUserHasListings || hasOrders;

      const emailUnverified = !!currentUser?.id && !currentUser?.attributes?.emailVerified;
      const emailVerificationNeeded = hasListingsOrOrders && emailUnverified;

      // Show reminder
      if (emailVerificationNeeded) {
        this.setState({ showMissingInformationReminder: EMAIL_VERIFICATION });
      }
    }
  }

  render() {
    const {
      rootClassName,
      className,
      containerClassName,
      currentUser,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      onManageDisableScrolling,
      onResendVerificationEmail,
    } = this.props;

    const user = ensureCurrentUser(currentUser);
    const classes = classNames(rootClassName || css.root, className);

    let content = null;

    const userType = user?.attributes?.profile?.publicData?.userType;
    const isGuest = !userType || userType === 'customer' || userType === 'guest' || userType !== 'service_provider';

    const handleClose = () => {
      const userId = getUserId(user);
      if (userId) {
        setEmailVerificationDismissed(userId);
      }
      this.setState({
        showMissingInformationReminder: null,
        hasSeenMissingInformationReminder: true,
      });
    };

    const currentUserLoaded = user && user.id;
    if (currentUserLoaded && isUserAuthorized(currentUser)) {
      if (this.state.showMissingInformationReminder === CALENDAR_SYNC) {
        content = (
          <CalendarSyncReminder
            className={classes}
            onClose={handleClose}
          />
        );
      } else if (this.state.showMissingInformationReminder === EMAIL_VERIFICATION && !isGuest) {
        content = (
          <EmailReminder
            className={classes}
            user={user}
            onResendVerificationEmail={onResendVerificationEmail}
            sendVerificationEmailInProgress={sendVerificationEmailInProgress}
            sendVerificationEmailError={sendVerificationEmailError}
          />
        );
      }
    }

    const closeButtonMessage = (
      <FormattedMessage id="ModalMissingInformation.closeVerifyEmailReminder" />
    );

    return (
      <Modal
        id="MissingInformationReminder"
        scrollLayerClassName={css.scrollLayer}
        containerClassName={classNames(css.container, containerClassName)}
        isOpen={!!this.state.showMissingInformationReminder}
        onClose={handleClose}
        usePortal
        lightCloseButton
        onManageDisableScrolling={onManageDisableScrolling}
        closeButtonMessage={closeButtonMessage}
      >
        {content}
      </Modal>
    );
  }
}

/**
 * Modal that tells user that they have not saved all the information the service needs.
 * This is used to remind user that they need to verify their email address.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.containerClassName overwrite components own css.container
 * @param {string} props.id
 * @param {Object} props.currentUser API entity
 * @param {Function} props.onManageDisableScrolling
 * @param {Object?} props.sendVerificationEmailError
 * @param {boolean} props.sendVerificationEmailInProgress
 * @returns {JSX.Element} Modal element if user needs to be reminded
 */
const EnhancedModalMissingInformation = props => {
  const routeConfiguration = useRouteConfiguration();

  return <ModalMissingInformation routeConfiguration={routeConfiguration} {...props} />;
};

export default EnhancedModalMissingInformation;
